import { Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useParams, useSearchParams } from "react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import PageWrapper from "@/components/PageWrapper.tsx";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler.tsx";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";
import {
  useLessonDetailOptions,
  useLessonKeywordOptions,
} from "./testing.api.ts";
import { sanitizeHtml } from "@/shared/utils/sanitizeHtml";
import { Maximize2, Minimize2, Pause, Play, Volume2, VolumeX } from "lucide-react";

// Keyword overlay surfaces somewhere in the middle of the video — past the
// intro, but not forcing the user to the very end. Re-rolled on every watch.
const KEYWORD_REVEAL_MIN = 0.3;
const KEYWORD_REVEAL_MAX = 0.8;
const KEYWORD_VISIBLE_SECONDS = 7;
const pickRevealFraction = () =>
  KEYWORD_REVEAL_MIN + Math.random() * (KEYWORD_REVEAL_MAX - KEYWORD_REVEAL_MIN);

const usesCustomVideoFullscreen = /Android|iPhone|iPad|iPod/i.test(
  navigator.userAgent
);

const getKeywordCode = (value?: string) => value?.trim().toUpperCase() || "";

const formatVideoTime = (value: number) => {
  if (!Number.isFinite(value)) return "0:00";
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const LessonDetailPage = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<LessonDetailLoading />}>
        <BaseLessonDetailPage />
      </Suspense>
    </ErrorBoundary>
  );
};

const LessonDetailLoading = () => (
  <PageWrapper>
    <div className="container pt-2 pb-6">
      <div className="skeleton w-full aspect-video rounded-xl mb-4" />
      <div className="skeleton w-3/4 h-6 rounded-md mb-3" />
      <div className="skeleton w-full h-4 rounded-md mb-2" />
      <div className="skeleton w-5/6 h-4 rounded-md mb-2" />
      <div className="skeleton w-4/6 h-4 rounded-md" />
    </div>
  </PageWrapper>
);

const BaseLessonDetailPage = () => {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get("collection");
  const { data: lesson } = useSuspenseQuery(useLessonDetailOptions(lessonId));

  // Per-user keyword shown over the video. Fetched whenever the feature is on
  // for this lesson — including after completion, so it still surfaces on every
  // re-watch. The code is stable (it's the user's verifiable keyword); only the
  // moment it appears changes.
  const keywordActive = lesson.has_keyword;
  const { data: keyword } = useQuery(
    useLessonKeywordOptions(lessonId, keywordActive)
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoShellRef = useRef<HTMLDivElement>(null);
  // Overlay position is re-randomized on every watch (incl. re-watch) so the
  // keyword surfaces at a different random moment each time.
  const revealAtRef = useRef(pickRevealFraction());
  const keywordShownRef = useRef(false);
  const [showKeyword, setShowKeyword] = useState(false);
  const [isVideoFallbackFullscreen, setIsVideoFallbackFullscreen] =
    useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Start of a fresh watch → pick a new random moment and allow showing again.
  const resetReveal = () => {
    revealAtRef.current = pickRevealFraction();
    keywordShownRef.current = false;
    setShowKeyword(false);
  };

  const handlePlay = () => {
    // Playing from the very beginning counts as a new watch / re-watch.
    const video = videoRef.current;
    if (video && video.currentTime < 1) resetReveal();
    setIsVideoPlaying(true);
  };

  const handlePause = () => {
    setIsVideoPlaying(false);
  };

  const syncVideoTiming = () => {
    const video = videoRef.current;
    if (!video) return;
    setVideoCurrentTime(video.currentTime || 0);
    setVideoDuration(video.duration || 0);
  };

  // Reveal the keyword overlay once playback crosses this watch's random
  // fraction, then auto-hide it after a few seconds.
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    syncVideoTiming();
    if (!video || !keyword || keywordShownRef.current) return;
    if (!video.duration || Number.isNaN(video.duration)) return;
    if (video.currentTime / video.duration >= revealAtRef.current) {
      keywordShownRef.current = true;
      setShowKeyword(true);
      window.setTimeout(() => setShowKeyword(false), KEYWORD_VISIBLE_SECONDS * 1000);
    }
  };

  const toggleVideoFallbackFullscreen = () => {
    setIsVideoFallbackFullscreen((value) => !value);
  };

  const closeVideoFallbackFullscreen = () => {
    setIsVideoFallbackFullscreen(false);
  };

  const toggleVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const toggleVideoMuted = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsVideoMuted(video.muted);
  };

  const seekVideo = (value: number) => {
    const video = videoRef.current;
    if (!video || !videoDuration) return;
    video.currentTime = value;
    setVideoCurrentTime(value);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsVideoFallbackFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Mark this lesson as locally watched so the list page can flip the
  // button to "Enter keyword" without a backend round-trip. Persisted
  // via sessionStorage — list page reads on mount.
  useEffect(() => {
    if (!lessonId) return;
    try {
      sessionStorage.setItem(`lesson_watched_${lessonId}`, "1");
    } catch {
      // sessionStorage may be unavailable in privacy mode — no-op.
    }
  }, [lessonId]);

  const backPath = collectionId
    ? `/testing?collection=${collectionId}`
    : "/testing";

  return (
    <PageWrapper backPath={backPath}>
      <div
        className="fixed inset-0 z-10 flex flex-col bg-[#0e0e14]"
        style={{
          paddingTop: "var(--safe-area-inset-top, 0px)",
          paddingBottom: "var(--safe-area-inset-bottom, 0px)",
        }}
      >
        {lesson.video ? (
          <div
            ref={videoShellRef}
            className={
              isVideoFallbackFullscreen
                ? "fixed inset-0 z-[10000] bg-black"
                : "shrink-0 bg-black border-b border-white/08"
            }
          >
            <div
              className={
                isVideoFallbackFullscreen
                  ? "relative flex size-full items-center justify-center bg-black"
                  : "relative mx-auto md:max-w-3xl lg:max-w-4xl"
              }
            >
              <video
                ref={videoRef}
                src={lesson.video.url}
                poster={lesson.thumbnail?.url || undefined}
                autoPlay
                controls={!usesCustomVideoFullscreen}
                controlsList={usesCustomVideoFullscreen ? "nofullscreen" : undefined}
                playsInline
                preload="metadata"
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={resetReveal}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={syncVideoTiming}
                onDurationChange={syncVideoTiming}
                onVolumeChange={() => {
                  const video = videoRef.current;
                  if (video) setIsVideoMuted(video.muted);
                }}
                className={
                  isVideoFallbackFullscreen
                    ? "lesson-keyword-video h-full w-full object-contain bg-black"
                    : "lesson-keyword-video w-full aspect-video object-contain bg-black"
                }
              />

              {usesCustomVideoFullscreen && (
                <div
                  className={`absolute inset-x-0 bottom-0 z-[10002] px-2.5 pb-[max(10px,var(--safe-area-inset-bottom,0px))] pt-8 bg-gradient-to-t from-black/75 via-black/30 to-transparent ${
                    isVideoFallbackFullscreen ? "" : "rounded-b-none"
                  }`}
                >
                  <div className="flex items-center gap-2 rounded-lg bg-black/65 px-2.5 py-1.5 text-white backdrop-blur">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVideoPlayback();
                      }}
                      className="flex-center size-8 shrink-0 rounded-full bg-white/12 active:bg-white/20"
                      aria-label={isVideoPlaying ? "Pause" : "Play"}
                    >
                      {isVideoPlaying ? (
                        <Pause className="size-4" fill="currentColor" />
                      ) : (
                        <Play className="size-4" fill="currentColor" />
                      )}
                    </button>

                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                      <span className="w-8 text-right text-[10px] tabular-nums text-white/80">
                        {formatVideoTime(videoCurrentTime)}
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={videoDuration || 0}
                        step={0.1}
                        value={Math.min(videoCurrentTime, videoDuration || 0)}
                        onChange={(e) => seekVideo(Number(e.target.value))}
                        className="h-1 min-w-0 flex-1 accent-[#FBC76C]"
                        aria-label="Video progress"
                      />
                      <span className="w-8 text-[10px] tabular-nums text-white/80">
                        {formatVideoTime(videoDuration)}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVideoMuted();
                      }}
                      className="flex-center size-8 shrink-0 rounded-full bg-white/12 active:bg-white/20"
                      aria-label={isVideoMuted ? "Unmute" : "Mute"}
                    >
                      {isVideoMuted ? (
                        <VolumeX className="size-4" />
                      ) : (
                        <Volume2 className="size-4" />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isVideoFallbackFullscreen) {
                          closeVideoFallbackFullscreen();
                        } else {
                          toggleVideoFallbackFullscreen();
                        }
                      }}
                      className="flex-center size-8 shrink-0 rounded-full bg-white/12 active:bg-white/20"
                      aria-label={
                        isVideoFallbackFullscreen
                          ? t("close") || "Yopish"
                          : t("fullscreen") || "Fullscreen"
                      }
                    >
                      {isVideoFallbackFullscreen ? (
                        <Minimize2 className="size-4" strokeWidth={2.4} />
                      ) : (
                        <Maximize2 className="size-4" strokeWidth={2.4} />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {showKeyword && keyword && (
                <div
                  className={`pointer-events-none absolute left-1/2 z-[10001] -translate-x-1/2 animate-in fade-in slide-in-from-top-2 ${
                    isVideoFallbackFullscreen ? "top-24" : "top-3"
                  }`}
                >
                  <div className="flex flex-col items-center gap-1 rounded-xl border border-amber-400/40 bg-black/75 px-4 py-2 backdrop-blur-sm">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-amber-300/90">
                      {t("keyword") || "Kalit so'z"}
                    </span>
                    <span className="text-2xl font-bold tracking-[0.3em] text-white">
                      {getKeywordCode(keyword.code)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="shrink-0 mx-4 mt-4 rounded-xl bg-white/08 aspect-video flex-center text-gray-1 text-sm">
            {t("video_not_available") || "Video mavjud emas"}
          </div>
        )}

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="container py-4">
            <h1 className="text-xl font-bold text-white leading-130 mb-3">
              {lesson.name}
            </h1>

            {lesson.description && (
              <p className="text-sm text-white/80 leading-150 mb-4">
                {lesson.description}
              </p>
            )}

            {lesson.content && (
              <div
                className="voucher-richtext text-sm text-white/90 leading-[170%]"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.content) }}
              />
            )}

            {!lesson.content && !lesson.description && (
              <p className="text-sm text-gray-1 text-center py-6">
                {t("script_coming_soon") || "Tavsif tez orada qo'shiladi"}
              </p>
            )}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default LessonDetailPage;
